import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { validatePassword, getPasswordInfo } from '../utils/passwordGenerator';

const PasswordContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, rgb(24, 29, 49) 0%, rgb(21, 10, 32) 100%);
  padding: 20px;
`;

const PasswordCard = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  padding: 40px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  max-width: 400px;
  width: 100%;
  text-align: center;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
`;

const Title = styled.h1`
  color: #fff;
  font-size: 2rem;
  margin-bottom: 10px;
  font-weight: 300;
`;

const Subtitle = styled.p`
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 30px;
  font-size: 1rem;
`;

const InputGroup = styled.div`
  margin-bottom: 20px;
`;

const Input = styled.input`
  width: 100%;
  padding: 15px;
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
  font-size: 1rem;
  text-align: center;
  letter-spacing: 2px;
  font-weight: bold;
  text-transform: uppercase;
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
    text-transform: none;
    letter-spacing: normal;
    font-weight: normal;
  }
  
  &:focus {
    outline: none;
    border-color: #00d4ff;
    box-shadow: 0 0 20px rgba(0, 212, 255, 0.3);
  }
`;

const Button = styled.button`
  width: 100%;
  padding: 15px;
  background: linear-gradient(45deg, #00d4ff, #0066cc);
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(0, 212, 255, 0.4);
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0, 212, 255, 0.5);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const ErrorMessage = styled.div`
  color: #ffaa00;
  margin-top: 15px;
  font-size: 0.9rem;
  padding: 10px;
  background: rgba(255, 170, 0, 0.1);
  border-radius: 8px;
  border: 1px solid rgba(255, 170, 0, 0.3);
  font-family: 'Courier New', monospace;
`;

const PasswordProtection = ({ children, onAuthenticated }) => {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // Проверяем, есть ли сохраненная аутентификация в localStorage
    const savedAuth = localStorage.getItem('enode-auth');
    if (savedAuth) {
      const authData = JSON.parse(savedAuth);
      const currentPasswordInfo = getPasswordInfo();
      
      // Если аутентификация была в текущем 15-дневном периоде, автоматически входим
      if (authData.periodNumber === currentPasswordInfo.periodNumber && authData.authenticated) {
        setIsAuthenticated(true);
        if (onAuthenticated) onAuthenticated();
        return;
      } else {
        // Очищаем устаревшую аутентификацию
        localStorage.removeItem('enode-auth');
      }
    }
  }, [onAuthenticated]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    // Небольшая задержка для UX
    await new Promise(resolve => setTimeout(resolve, 500));

    if (validatePassword(password)) {
      setIsAuthenticated(true);
      
      // Сохраняем аутентификацию в localStorage
      const currentPasswordInfo = getPasswordInfo();
      localStorage.setItem('enode-auth', JSON.stringify({
        periodNumber: currentPasswordInfo.periodNumber,
        authenticated: true,
        timestamp: new Date().toISOString()
      }));
      
      if (onAuthenticated) onAuthenticated();
    } else {
      setError('Неверный пароль. Попробуйте еще раз.');
      setPassword('');
    }
    
    setIsLoading(false);
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setPassword('');
    setError('');
    localStorage.removeItem('enode-auth');
  };

  // Если пользователь аутентифицирован, показываем контент
  if (isAuthenticated) {
    return (
      <>
        {children}
        <LogoutButton onClick={handleLogout}>
          Выйти
        </LogoutButton>
      </>
    );
  }

  // Показываем форму входа
  return (
    <PasswordContainer>
      <PasswordCard>
        <Title>🔐 Доступ к сайту</Title>
        <Subtitle>Введите пароль для продолжения</Subtitle>
        
        <form onSubmit={handleSubmit}>
          <InputGroup>
            <Input
              type="text"
              value={password}
              onChange={(e) => setPassword(e.target.value.toUpperCase())}
              placeholder="Введите пароль"
              maxLength="8"
              required
              autoFocus
            />
          </InputGroup>
          
          <Button type="submit" disabled={isLoading}>
            {isLoading ? 'Проверка...' : 'Войти'}
          </Button>
        </form>
        
        {error && <ErrorMessage>{error}</ErrorMessage>}
      
      </PasswordCard>
    </PasswordContainer>
  );
};

const LogoutButton = styled.button`
  position: fixed;
  top: 20px;
  right: 20px;
  padding: 10px 20px;
  background: rgba(255, 170, 0, 0.2);
  color: #ffaa00;
  border: 1px solid rgba(255, 170, 0, 0.3);
  border-radius: 20px;
  cursor: pointer;
  font-size: 0.9rem;
  font-family: 'Courier New', monospace;
  text-transform: uppercase;
  letter-spacing: 1px;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  
  &:hover {
    background: rgba(255, 170, 0, 0.3);
    transform: translateY(-2px);
  }
`;

export default PasswordProtection;
